// ./src/pages/private/EventGuestsPage/RsvpGroupMembersDrawer.tsx
//
// View + rearrange the members of one RSVP group. People in a group RSVP together and count as a
// single party. The host can set the main guest, remove someone (unlinks them from the group), or
// add another of the event's guests (moves them into this group). Opened from the guest edit
// drawer's left-footer "Group members" action. All changes apply immediately (no Save).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Stack, Group, Text, Paper, Avatar, Box, Badge, Button, ActionIcon, Loader, Center, Divider, Tooltip } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import { AppDrawer, AppIcon, SearchInput } from "@/components/generic";
import { EventGuest, EventGuestService, getGuestDisplayName, getGuestEmail, listAllPages } from "@/utils/api";
import { useToast } from "@/hooks";
import { refusalReason } from "@/utils";

export interface RsvpGroupMembersDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  rsvpGroupId: string;
  groupName?: string | null;
  /** Called after any change so the parent can refresh (the group's headcount may have changed). */
  onChanged?: () => void;
}

export const RsvpGroupMembersDrawer: React.FC<RsvpGroupMembersDrawerProps> = ({
  opened,
  onClose,
  eventId,
  rsvpGroupId,
  groupName,
  onChanged,
}) => {
  const { t } = useTranslation(["events", "common"]);
  const toast = useToast();

  const [members, setMembers] = useState<EventGuest[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search, 300);
  const [candidates, setCandidates] = useState<EventGuest[]>([]);
  const [searching, setSearching] = useState(false);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    const res = await listAllPages(EventGuestService.list, { eventId: [eventId], rsvpGroupId: [rsvpGroupId] });
    setMembers(res.code === OP_RESULT_CODES.OK && Array.isArray(res.data) ? (res.data as EventGuest[]) : []);
    setLoading(false);
  }, [eventId, rsvpGroupId]);

  useEffect(() => {
    if (!opened) {
      return;
    }
    setSearch("");
    setCandidates([]);
    void loadMembers();
  }, [opened, loadMembers]);

  const memberIds = useMemo(() => new Set(members.map((m) => String(m.id))), [members]);

  // Add-picker: the event's guests matching the search, excluding those already in this group.
  useEffect(() => {
    if (!opened || !debounced.trim()) {
      setCandidates([]);
      setSearching(false);
      return;
    }
    let alive = true;
    setSearching(true);
    EventGuestService.list({ eventId: [eventId], searchKeyword: debounced.trim() }, { page: 1, pageSize: 25 })
      .then((res) => {
        if (!alive) {
          return;
        }
        const rows = res.code === OP_RESULT_CODES.OK && Array.isArray(res.data) ? (res.data as EventGuest[]) : [];
        setCandidates(rows.filter((g) => g.id && !memberIds.has(String(g.id))));
      })
      .finally(() => {
        if (alive) {
          setSearching(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [opened, debounced, eventId, memberIds]);

  const refresh = async () => {
    await loadMembers();
    onChanged?.();
  };

  const addMember = async (g: EventGuest) => {
    setBusyId(String(g.id));
    // Moving a guest into a group makes them a single head — the group holds the party count.
    const res = await EventGuestService.update({ id: g.id! }, { rsvpGroupId, partySize: 1 });
    setBusyId(null);
    if (!res.didSucceed()) {
      toast.error(refusalReason(res) || t("events:guests.rsvpGroupAddFailed", { defaultValue: "Could not add to the group." }));
      return;
    }
    setSearch("");
    await refresh();
  };

  const removeMember = async (g: EventGuest) => {
    setBusyId(String(g.id));
    const res = await EventGuestService.update({ id: g.id! }, { rsvpGroupId: null, isGroupPrimary: false });
    setBusyId(null);
    if (!res.didSucceed()) {
      toast.error(refusalReason(res) || t("events:guests.rsvpGroupRemoveFailed", { defaultValue: "Could not remove from the group." }));
      return;
    }
    await refresh();
  };

  const setPrimary = async (g: EventGuest) => {
    setBusyId(String(g.id));
    // One main guest per group: clear the current primary/-ies, then set this one.
    const others = members.filter((m) => m.id !== g.id && m.isGroupPrimary).map((m) => m.id as string);

    if (others.length > 0) {
      const cleared = await EventGuestService.bulkUpdate(others, { isGroupPrimary: false });

      // ⚠ This result used to be thrown away. When the clear fails and the set below
      // succeeds, the group ends up with TWO main guests and the host is told nothing —
      // the only clue is a second "Main" badge appearing.
      if (!cleared.didSucceed()) {
        setBusyId(null);
        toast.error(
          refusalReason(cleared) || t("events:guests.rsvpGroupPrimaryFailed", { defaultValue: "Could not set the main guest." })
        );

        return;
      }
    }

    const res = await EventGuestService.update({ id: g.id! }, { isGroupPrimary: true });
    setBusyId(null);
    if (!res.didSucceed()) {
      toast.error(refusalReason(res) || t("events:guests.rsvpGroupPrimaryFailed", { defaultValue: "Could not set the main guest." }));
      return;
    }
    await refresh();
  };

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={groupName ? t("events:guests.rsvpGroupTitle", { defaultValue: "Group: {{name}}", name: groupName }) : t("events:guests.rsvpGroupMembers", { defaultValue: "Group members" })}
      size="md"
      actionType="close"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t("events:guests.rsvpGroupMembersHint", {
            defaultValue: "People in this group RSVP together and count as one party. Add or remove members, or set the main guest.",
          })}
        </Text>

        {loading ? (
          <Center py="lg">
            <Loader size="sm" />
          </Center>
        ) : members.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            {t("events:guests.rsvpGroupEmpty", { defaultValue: "No one in this group yet." })}
          </Text>
        ) : (
          <Stack gap={4}>
            {members.map((m) => (
              <Paper key={String(m.id)} withBorder radius="md" p="sm">
                <Group justify="space-between" wrap="nowrap" gap="sm">
                  <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                    <Avatar name={getGuestDisplayName(m)} color="initials" radius="xl" size={34} />
                    <Box style={{ minWidth: 0 }}>
                      <Group gap="xs" wrap="nowrap">
                        <Text fw={500} lineClamp={1}>
                          {getGuestDisplayName(m)}
                        </Text>
                        {m.isGroupPrimary && (
                          <Badge size="sm" color="blue" variant="light">
                            {t("events:guests.rsvpGroupMain", { defaultValue: "Main" })}
                          </Badge>
                        )}
                      </Group>
                      {getGuestEmail(m) && (
                        <Text size="sm" c="dimmed" lineClamp={1}>
                          {getGuestEmail(m)}
                        </Text>
                      )}
                    </Box>
                  </Group>
                  <Group gap="xs" wrap="nowrap">
                    {!m.isGroupPrimary && (
                      <Tooltip label={t("events:guests.rsvpGroupSetMain", { defaultValue: "Set as main guest" })} withArrow>
                        <ActionIcon variant="subtle" color="gray" size="lg" onClick={() => setPrimary(m)} loading={busyId === String(m.id)} aria-label={t("events:guests.rsvpGroupSetMain", { defaultValue: "Set as main guest" })}>
                          <AppIcon iconName="Star" size={18} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    <Tooltip label={t("events:guests.rsvpGroupRemove", { defaultValue: "Remove from group" })} withArrow>
                      <ActionIcon variant="subtle" color="gray" size="lg" onClick={() => removeMember(m)} loading={busyId === String(m.id)} aria-label={t("events:guests.rsvpGroupRemove", { defaultValue: "Remove from group" })}>
                        <AppIcon iconName="X" size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Group>
              </Paper>
            ))}
          </Stack>
        )}

        <Divider label={t("events:guests.rsvpGroupAdd", { defaultValue: "Add someone to this group" })} labelPosition="center" />

        <SearchInput
          value={search}
          onChange={setSearch}
          debounceDelay={300}
          placeholder={t("events:guests.rsvpGroupSearch", { defaultValue: "Search this event's guests…" })}
        />

        {searching ? (
          <Center py="sm">
            <Loader size="sm" />
          </Center>
        ) : search.trim() && candidates.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="sm">
            {t("events:guests.rsvpGroupNoMatch", { defaultValue: "No other guests match your search." })}
          </Text>
        ) : (
          <Stack gap={4}>
            {candidates.map((g) => (
              <Paper key={String(g.id)} withBorder radius="md" p="sm">
                <Group justify="space-between" wrap="nowrap" gap="sm">
                  <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                    <Avatar name={getGuestDisplayName(g)} color="initials" radius="xl" size={34} />
                    <Box style={{ minWidth: 0 }}>
                      <Text fw={500} lineClamp={1}>
                        {getGuestDisplayName(g)}
                      </Text>
                      {getGuestEmail(g) && (
                        <Text size="sm" c="dimmed" lineClamp={1}>
                          {getGuestEmail(g)}
                        </Text>
                      )}
                    </Box>
                  </Group>
                  <Button
                    size="compact-md"
                    variant="light"
                    leftSection={<AppIcon iconName="Plus" size={14} />}
                    onClick={() => addMember(g)}
                    loading={busyId === String(g.id)}
                  >
                    {t("common:buttons.add", { defaultValue: "Add" })}
                  </Button>
                </Group>
              </Paper>
            ))}
          </Stack>
        )}
      </Stack>
    </AppDrawer>
  );
};
